// Deterministic 2D facial geometry (pipeline Stage 2) — tuning constants.
//
// Computed from the MediaPipe FaceMesh landmarks facePrep already produces (in
// output-frame fractions of the normalized 1080x1440 aligned crop). These are
// FRONTAL 2D estimates only — profile-dependent traits (gonial angle, E-line,
// chin projection) are out of scope and intentionally NOT measured here.
//
// Ideal/tolerance values are approximate, literature-informed, and CURRENTLY
// GENDER-NEUTRAL. They power a diagnostic "what's strong / what's addressable"
// read — NOT an objective verdict. The Gemini score remains the headline.
// TODO(phase-1): gender/ethnicity-aware norm tables (needs a gender signal).

// MediaPipe FaceMesh canonical landmark indices (0..467).
export const LM = Object.freeze({
  TRICHION: 10, // hairline mid (forehead top) — proxy
  GLABELLA: 9, // between brows
  SUBNASALE: 2, // base of nose
  MENTON: 152, // chin bottom
  NASION: 168, // nose bridge top (between eyes) — midline anchor

  L_EYE_OUTER: 33,
  L_EYE_INNER: 133,
  R_EYE_INNER: 362,
  R_EYE_OUTER: 263,

  L_CHEEK: 234, // left face contour (zygomatic)
  R_CHEEK: 454, // right face contour

  L_BROW: 105,
  R_BROW: 334,

  MOUTH_L: 61,
  MOUTH_R: 291,
  UPPER_LIP_TOP: 0,
  UPPER_LIP_BOT: 13,
  LOWER_LIP_TOP: 14,
  LOWER_LIP_BOT: 17,
});

// Mirror pairs for the symmetry read (left index, right index).
export const SYMMETRY_PAIRS = Object.freeze([
  [LM.L_EYE_OUTER, LM.R_EYE_OUTER],
  [LM.L_EYE_INNER, LM.R_EYE_INNER],
  [LM.L_BROW, LM.R_BROW],
  [LM.MOUTH_L, LM.MOUTH_R],
  [LM.L_CHEEK, LM.R_CHEEK],
]);

// Per-metric scoring: score = 100 * exp(-((value - ideal)/tolerance)^2).
// `key` is the metric id; `label` is the editorial display name.
// `ideal` is the literature target; `tolerance` sets how fast the score decays.
export const METRICS = Object.freeze({
  thirds: { label: 'FACIAL THIRDS', ideal: 0, tolerance: 0.06, unit: 'dev' },
  fifths: { label: 'FACIAL FIFTHS', ideal: 0, tolerance: 0.05, unit: 'dev' },
  canthal_tilt: { label: 'CANTHAL TILT', ideal: 6, tolerance: 5, unit: 'deg' },
  fwhr: { label: 'FACIAL WIDTH-HEIGHT', ideal: 1.9, tolerance: 0.25, unit: 'ratio' },
  esr: { label: 'EYE SPACING', ideal: 0.46, tolerance: 0.05, unit: 'ratio' },
  symmetry: { label: 'SYMMETRY', ideal: 0, tolerance: 0.04, unit: 'dev' },
  lip_ratio: { label: 'LIP BALANCE', ideal: 1.6, tolerance: 0.5, unit: 'ratio' },
});

// Ordered metric keys (display + iteration order). Sourced from the shared
// contract so the backend whitelist and the frontend lib can't drift.
export { GEOMETRY_METRIC_KEYS as METRIC_KEYS } from '../../contracts/oracleApi';

// Score at/above this reads as "strong"; below reads as "addressable".
export const STRONG_THRESHOLD = 70;
